import { COOKIE_KEY, SEARCH_PARAMS, STORAGE_KEY } from "@constants";
import { MarketingData, MarketingDataParam } from "@types";
import { getCookie, setCookie } from "@utils/cookie";
import logger from "@utils/logger";
import { storage } from "kz-ui-sdk";

const MKTG_VERSION = "1.0.0";
// Version in the name: a bump ignores old cookies, also the copy a fresh iOS app starts with
const MKTG_COOKIE = `${COOKIE_KEY.MARKETING_DATA}-${MKTG_VERSION}`;

/** The installed iOS app starts with empty storage but gets a copy of the browser's cookies. */
const MKTG_COOKIE_MAX_AGE = 7 * 24 * 60 * 60; // 7 days, in seconds
const MKTG_KEYS = new Set<string>(Object.values(MarketingDataParam));

/** Notification links carry a one-time login token; it must not be stored or sent on with the URL. */
const withoutFastLoginToken = (href: string) => {
  const url = new URL(href);
  if (!url.searchParams.has(SEARCH_PARAMS.FAST_LOGIN_TOKEN)) return href;
  url.searchParams.delete(SEARCH_PARAMS.FAST_LOGIN_TOKEN);
  return url.toString();
};

/** This document's entry URL. The collector re-runs on later URLs, and the stored blob outlives the visit. */
const landingUrl = withoutFastLoginToken(window.location.href);

export const getLandingUrl = () => landingUrl;

/** Mirrored in memory: iOS WebKit can drop localStorage mid-session. The sdk helper is unguarded. */
let mirror: Partial<MarketingData> | null = null;

const storeLocally = (data: Partial<MarketingData>): void => {
  mirror = data;
  try {
    storage.local.setItem(STORAGE_KEY.MARKETING_DATA, data, MKTG_VERSION);
  } catch (error) {
    logger._console.error(error);
  }
};

const readCookie = (): Partial<MarketingData> | null => {
  const raw = getCookie(MKTG_COOKIE);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const entries = Object.entries(parsed).filter(([key, value]) => MKTG_KEYS.has(key) && typeof value === "string");
    return entries.length ? (Object.fromEntries(entries) as Partial<MarketingData>) : null;
  } catch {
    return null;
  }
};

export const getMarketingData = (): Partial<MarketingData> | null => {
  // Mirror first: it is never staler than the store, and a thrown write leaves the store stale
  if (mirror) return mirror;
  try {
    const stored = storage.local.getItem(STORAGE_KEY.MARKETING_DATA) as Partial<MarketingData> | null;
    if (stored) return stored;
  } catch (error) {
    logger._console.error(error);
  }
  const fromCookie = readCookie();
  if (fromCookie) storeLocally(fromCookie);
  return fromCookie;
};

export const setMarketingData = (data: Partial<MarketingData>): void => {
  storeLocally(data);
  setCookie(MKTG_COOKIE, JSON.stringify(data), MKTG_COOKIE_MAX_AGE);
};

export const invalidateMarketingData = (): void => {
  try {
    storage.local.invalidate(STORAGE_KEY.MARKETING_DATA, MKTG_VERSION);
  } catch (error) {
    logger._console.error(error);
  }
};
